import { sanitizeKey } from "../../../../lib/utils/format";

/**
 * Chart Data Utilities
 *
 * Transforms raw deal data into yearly aggregated metrics for visualization.
 *
 * Key transformations:
 * - Groups deals by year
 * - Calculates per-category count and volume metrics
 * - Applies sanitized key naming for safe object property access
 * - Rounds volumes to one decimal place for clean display
 *
 * Business Logic Notes:
 * - H1/H2 detection now handled via API's currentHalf field (not Quarter data)
 * - "Other" category always sorts last for visual consistency
 * - Grand totals computed from allData parameter for accurate cross-category sums
 */

export interface DealItem {
  Year?: number | string;
  Industry?: string;
  Canton?: string;
  Gender?: string;
  Phase?: string;
  VolumeMChf?: number;
  [key: string]: unknown;
}

export type YearData = {
  year: number;
  totalCount?: number;
  totalVolume?: number;
  __grandTotalCount?: number;
  __grandTotalVolume?: number;
} & Record<string, number>;

export interface YearlyDataConfig<T> {
  groupByField?: keyof T | string;
  categories: string[];
  getCategoryValue: (item: T) => string | null | undefined;
  getVolumeValue?: (item: T) => number;
  includeTotal?: boolean;
  allData?: T[] | null;
}

/**
 * Groups data items by year for temporal aggregation
 */
export const groupDataByYear = <T extends Record<string, any>>(
  data: T[] | null | undefined,
  groupByField: keyof T | string = "Year"
): Record<string, T[]> => {
  if (!data || !Array.isArray(data)) return {};

  return data.reduce<Record<string, T[]>>((acc, item) => {
    const yearValue = item[groupByField as string];
    if (!yearValue) return acc;
    const yearKey = String(yearValue);

    if (!acc[yearKey]) {
      acc[yearKey] = [];
    }
    acc[yearKey].push(item);
    return acc;
  }, {});
};

/**
 * Calculates yearly metrics (count/volume) per category with optional grand totals.
 *
 * Output format: Each year gets properties like:
 * - IndustryName__count: number of deals in this category
 * - IndustryName__volume: CHF millions invested
 * - totalCount/totalVolume: aggregates within filtered categories
 * - __grandTotalCount/__grandTotalVolume: totals across ALL categories (from allData)
 *
 * The double-underscore prefix distinguishes grand totals computed from allData
 * versus filtered totals from the main data parameter.
 */
export const calculateYearlyData = <T extends Record<string, any>>(
  data: T[],
  config: YearlyDataConfig<T>
): YearData[] => {
  const {
    groupByField = "Year",
    categories = [],
    getCategoryValue,
    getVolumeValue = (item: any) => (typeof item?.VolumeMChf === "number" ? item.VolumeMChf : 0),
    includeTotal = false,
    allData = null,
  } = config;

  const groupedByYear = groupDataByYear<T>(data, groupByField);
  const allGroupedByYear = allData ? groupDataByYear<T>(allData, groupByField) : groupedByYear;

  const allYears = new Set<string>([
    ...Object.keys(groupedByYear),
    ...(allData ? Object.keys(allGroupedByYear) : []),
  ]);

  return Array.from(allYears)
    .map((year): YearData => {
      const items = groupedByYear[year] || [];
      const yearData: YearData = { year: parseInt(year, 10) };

      // Calculate per-category metrics using sanitized keys for safe property access
      categories.forEach((category) => {
        const categoryItems = items.filter((item) => getCategoryValue(item) === category);

        const count = categoryItems.length;
        const volume = categoryItems.reduce((sum, item) => sum + getVolumeValue(item), 0);

        const sanitizedKey = sanitizeKey(category);
        yearData[`${sanitizedKey}__count`] = count;
        // Round to one decimal to avoid floating point display issues (e.g., 5.300000001)
        yearData[`${sanitizedKey}__volume`] = Math.round(volume * 10) / 10;
      });

      // Add grand totals computed from complete dataset (allData)
      // Used when "Show Total" is enabled to display aggregate trend line
      // Double-underscore prefix distinguishes grand totals (computed from allData)
      // from filtered totals (computed from main data parameter)
      if (includeTotal) {
        const allItemsForYear = allGroupedByYear[year] || [];
        const grandTotalCount = allItemsForYear.length;
        const grandTotalVolume = allItemsForYear.reduce(
          (sum, item) => sum + getVolumeValue(item),
          0
        );

        yearData.totalCount = grandTotalCount;
        yearData.totalVolume = Math.round(grandTotalVolume * 10) / 10;
        yearData.__grandTotalCount = grandTotalCount;
        yearData.__grandTotalVolume = Math.round(grandTotalVolume * 10) / 10;
      }

      return yearData;
    })
    .sort((a, b) => a.year - b.year);
};

export interface SimpleYearlyConfig<T> {
  groupByField?: keyof T | string;
  getVolumeValue?: (item: T) => number;
  countField?: string;
  volumeField?: string;
}

/**
 * Simplified yearly aggregation without categories.
 * Used for timeline overview charts showing total investment/deals per year.
 */
export const calculateSimpleYearlyData = <T extends Record<string, any>>(
  data: T[],
  config: SimpleYearlyConfig<T> = {}
): YearData[] => {
  const {
    groupByField = "Year",
    getVolumeValue = (item: any) => (typeof item?.VolumeMChf === "number" ? item.VolumeMChf : 0),
    countField = "count",
    volumeField = "volume",
  } = config;

  const groupedByYear = groupDataByYear<T>(data, groupByField);

  return Object.entries(groupedByYear)
    .map(([year, items]): YearData => {
      const count = items.length;
      const volume = items.reduce((sum, item) => sum + getVolumeValue(item), 0);

      return {
        year: parseInt(year, 10),
        [countField]: count,
        [volumeField]: Math.round(volume * 10) / 10,
        totalCount: count,
        totalVolume: Math.round(volume * 10) / 10,
      } as YearData;
    })
    .sort((a, b) => a.year - b.year);
};

/**
 * Extracts unique categories from dataset with optional custom sorting.
 */
export const extractCategories = <T>(
  data: T[] | null | undefined,
  getCategoryValue: (item: T) => string | null | undefined,
  sortFn: ((a: string, b: string) => number) | null = null
): string[] => {
  if (!data || !Array.isArray(data)) return [];

  // Extract unique categories, filtering out null/undefined
  const categories = [...new Set(data.map(getCategoryValue).filter(Boolean))] as string[];

  return sortFn ? categories.sort(sortFn) : categories;
};

export const filterDataByCategories = <T>(
  data: T[],
  selectedCategories: string[] | null | undefined,
  getCategoryValue: (item: T) => string | null | undefined
): T[] => {
  if (!selectedCategories || selectedCategories.length === 0) return data;

  return data.filter((item) => {
    const category = getCategoryValue(item);
    return category ? selectedCategories.includes(category) : false;
  });
};

/**
 * Prepares chart data by flattening category metrics into direct properties.
 * Converts {Industry__volume: 5} to {Industry: 5} for easier D3 consumption.
 */
export const prepareChartSeries = (
  data: YearData[],
  categories: string[],
  metricSuffix = "__volume"
): YearData[] => {
  return data.map((yearData) => {
    const series: YearData = { ...yearData };

    categories.forEach((category) => {
      const sanitizedKey = sanitizeKey(category);
      const value = (yearData as any)[`${sanitizedKey}${metricSuffix}`] || 0;
      series[sanitizedKey] = value;
    });

    return series;
  });
};

export type ChartType = "industry" | "canton" | "gender" | "phase";

/**
 * Returns configuration template for different chart domains.
 * Specifies which field to extract categories from and how to compute values.
 */
export const getChartConfig = (chartType: ChartType | string): YearlyDataConfig<DealItem> => {
  const configs: Record<string, YearlyDataConfig<DealItem>> = {
    industry: {
      getCategoryValue: (item) => item.Industry ?? undefined,
      getVolumeValue: (item) => (typeof item.VolumeMChf === "number" ? item.VolumeMChf : 0),
      includeTotal: true,
      categories: [],
    },
    canton: {
      getCategoryValue: (item) => item.Canton ?? undefined,
      getVolumeValue: (item) => (typeof item.VolumeMChf === "number" ? item.VolumeMChf : 0),
      includeTotal: true,
      categories: [],
    },
    gender: {
      getCategoryValue: (item) => item.Gender ?? undefined,
      getVolumeValue: (item) => (typeof item.VolumeMChf === "number" ? item.VolumeMChf : 0),
      includeTotal: true,
      categories: [],
    },
    phase: {
      getCategoryValue: (item) => item.Phase ?? undefined,
      getVolumeValue: (item) => (typeof item.VolumeMChf === "number" ? item.VolumeMChf : 0),
      includeTotal: true,
      categories: [],
    },
  };

  return configs[chartType] || configs.industry;
};
